import { ApolloError } from '@apollo/client'
import { Alert } from '@designsforhealth/dfh-react-components'
import { Grid, TextField as MuiTextField } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { CustomerAddressFragment, GetCustomerAddressesQuery } from '../../../graphql/magento'
import useIsMounted from '../../../hooks/useIsMounted'
import useSiteSettings from '../../../hooks/useSiteSettings'
import {
  countryCodesWithMandatoryRegion,
  lookupKeyFromAddress,
  lookupKeyFromAddressFieldValues,
} from '../../../lib/cart/address'
import useSortedCustomerAddresses from '../../../lib/cart/hooks/useSortedCustomerAddresses'
import { RegionOptionsMap } from '../../../lib/cart/region-options'
import { AddressRestrictions, FormValues } from '../../../lib/cart/types'
import { SelectFieldOption } from '../../../lib/form'
import {
  addressMaxLengthProp,
  addressMaxLengthRule,
  addressNameRule,
  countryOptionsFor,
} from '../../../lib/form/address'
import { RegionSelectFieldOption } from '../../../lib/m2/address'
import { setValuesWith } from '../../../utils/formTools'
import ErrorRetrievingDataMessage from '../../ErrorRetrievingDataMessage'
import { RequiredSelectField, RequiredTextField } from '../../form/RequiredFields'
import SelectField from '../../form/SelectField'
import TextField from '../../form/TextField'
import { AddressStreetAutocompleteField, AutocompleteProps } from './AddressStreetAutocompleteField'
import SelectSavedAddress from './SelectSavedAddress'
import useStyles from './styles/addressForm'

const defaultValuesKeys: Array<keyof FormValues.Address> = [
  'city',
  'country_code',
  'firstname',
  'lastname',
  'postcode',
  'region',
  'street1',
  'street2',
  'telephone',
]

const fieldsToWatch = defaultValuesKeys

const simpleFieldsToSetFromSaved = [
  'city',
  'country_code',
  'firstname',
  'lastname',
  'postcode',
  'telephone',
] as const

export interface AddressFormProps {
  addressRestrictions?: AddressRestrictions
  autocompleteProps?: AutocompleteProps
  defaultValues?: FormValues.Address
  countriesLoading: boolean
  countriesError: ApolloError | undefined
  countryOptions: SelectFieldOption[]
  regionOptions: RegionSelectFieldOption[]
  regionOptionsMap: RegionOptionsMap
  showEmail?: boolean
}

type AddressesByKey = ReadonlyMap<string, CustomerAddressFragment>
type DeduplicatedAddresses = CustomerAddressFragment[]
type DeduplicatedAddressesTuple = [AddressesByKey, DeduplicatedAddresses]

/**
 * Make a map of addresses keyed by address hash, deduplicating addresses with the same hash.
 */
const makeAddressesByKey = (
  addresses: NonNullable<GetCustomerAddressesQuery['customer']>['addresses'],
): AddressesByKey => {
  if (!addresses) {
    return new Map()
  }
  return addresses.reduce((acc, address) => {
    if (address?.id != null) {
      const hash = lookupKeyFromAddress(
        address,
        // use `region` in lookup key when country does not have options defined
        address.region?.region_code ? 'region_code' : 'region',
      )
      if (!acc.has(hash)) {
        acc.set(hash, address)
      }
    }
    return acc
  }, new Map())
}

/**
 * Make a tuple of deduplicated addresses from map of addresses by lookup key
 */
const makeDeduplicatedTuple = (addressesByKey: AddressesByKey): DeduplicatedAddressesTuple => [
  addressesByKey,
  Array.from(addressesByKey.values()),
]

const AddressForm: React.FC<AddressFormProps> = ({
  addressRestrictions,
  autocompleteProps,
  defaultValues,
  countriesLoading,
  countriesError,
  countryOptions,
  regionOptions,
  regionOptionsMap,
  showEmail = false,
}) => {
  const { lang } = useSiteSettings() ?? {}
  const classes = useStyles()
  const { control, register, setValue, watch, reset } = useFormContext<FormValues.Address>()
  const [defaultAddressId, setDefaultAddressId] = useState<string | undefined>(undefined)
  const updateDefaultAddressIdTimeout = useRef<number | undefined>(undefined)
  const isMounted = useIsMounted()
  const fieldValues = watch(fieldsToWatch)

  const { addresses, loading, error } = useSortedCustomerAddresses(addressRestrictions)

  const [[addressesByKey, deduplicatedAddresses], setDeduplicatedTuple] =
    useState<DeduplicatedAddressesTuple>(() => makeDeduplicatedTuple(makeAddressesByKey(addresses)))

  useEffect(() => {
    setDeduplicatedTuple(makeDeduplicatedTuple(makeAddressesByKey(addresses)))
  }, [addresses])

  useEffect(() => {
    const updateDefaultAddressId = () => {
      if (!isMounted.current) {
        return
      }

      // reset timeout
      updateDefaultAddressIdTimeout.current = undefined

      const defaultAddressKey = lookupKeyFromAddressFieldValues(fieldValues)
      const result = addressesByKey.get(defaultAddressKey)?.id
      const value = result != null ? String(result) : undefined
      setDefaultAddressId(value)
      // console.log(`updateDefaultAddressId() => ${value}`)
    }

    // clear timeout if pending
    if (updateDefaultAddressIdTimeout.current) {
      clearTimeout(updateDefaultAddressIdTimeout.current)
    }

    // schedule update after brief delay to maintain responsiveness
    updateDefaultAddressIdTimeout.current = window.setTimeout(updateDefaultAddressId, 250)
  }, [addressesByKey, fieldValues, isMounted])

  useEffect(() => {
    if (!defaultValues) {
      return
    }

    const setDefaultAddressValues = (): void => {
      const setValues = setValuesWith(setValue)
      setValues(defaultValues)
    }

    window.requestAnimationFrame(setDefaultAddressValues)
  }, [defaultValues, setValue])

  const onSavedAddressSelected = useCallback(
    (selectedAddressId?: string): void => {
      if (!selectedAddressId || !addresses) {
        return
      }

      const setAddressValues = (selectedAddress: CustomerAddressFragment): void => {
        const simpleValues = simpleFieldsToSetFromSaved.reduce<FormValues.Address>((acc, field) => {
          const value = selectedAddress[field]
          acc[field] = value || ''
          return acc
        }, {})

        const street1Value = (selectedAddress.street || [])[0] || ''
        const street2Value = (selectedAddress.street || [])[1] || ''

        const selectedAddressRegionId = selectedAddress.region?.region_id
        const selectedAddressRegion = selectedAddress.region?.region
        const regionOptionsMatch =
          selectedAddressRegionId != null
            ? regionOptionsMap.getById(selectedAddressRegionId)
            : selectedAddressRegion
              ? regionOptionsMap.getByName(selectedAddressRegion)
              : undefined

        const regionValue = regionOptionsMatch
          ? regionOptionsMatch.value
          : selectedAddressRegion || ''

        const setValues = setValuesWith(setValue)

        setValues(
          {
            ...simpleValues,
            street1: street1Value,
            street2: street2Value,
            region: regionValue,
            region_id: regionOptionsMatch?.region.id ?? null,
          },
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        )
      }

      const matchAndSetValues = () => {
        const selectedAddress = addresses.find((address) =>
          address ? selectedAddressId === String(address.id) : false,
        )

        if (!selectedAddress) {
          return
        }
        window.requestAnimationFrame(() => setAddressValues(selectedAddress))
      }

      // set values in animation frame to maintain responsiveness
      window.requestAnimationFrame(matchAndSetValues)
    },
    [addresses, regionOptionsMap, setValue],
  )

  const onRegionChange = useCallback(
    (value: unknown): void => {
      // update `region_id` when selected option changes
      const selectedOption =
        typeof value === 'string' && value ? regionOptionsMap.getByCode(value) : null

      setValue('region_id', selectedOption?.region.id ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [regionOptionsMap, setValue],
  )

  const onCountryChange = useCallback((): void => {
    const setValues = setValuesWith(setValue)
    setValues(
      {
        city: '',
        region: '',
        region_id: null,
        postcode: '',
      },
      {
        shouldDirty: true,
        shouldValidate: false,
      },
    )
  }, [setValue])

  const isStateRequired = useMemo(() => {
    const { country_code: countryCode } = fieldValues
    return !!countryCode && countryCodesWithMandatoryRegion.includes(countryCode)
  }, [fieldValues])

  const {
    defaultOption: { value: defaultCountryCode },
  } = useMemo(() => countryOptionsFor(lang), [lang])

  const handleAddNewAddress = () => {
    reset({
      firstname: '',
      lastname: '',
      street1: '',
      street2: '',
      city: '',
      region: '',
      postcode: '',
      country_code: defaultCountryCode,
      telephone: '',
    })
  }

  return (
    <Grid
      className={classes.addressForm}
      container
      spacing={2}
      alignItems="center"
      justifyContent="center"
    >
      <Grid item xs={12}>
        {(countriesError || error) && (
          <Alert type="error">
            <ErrorRetrievingDataMessage />
          </Alert>
        )}
        {!!autocompleteProps?.parseSelectedPlaceError && (
          <Alert type="error">{autocompleteProps.parseSelectedPlaceError}</Alert>
        )}
      </Grid>
      {showEmail && (
        <Grid item xs={12} sm={12}>
          <RequiredTextField
            fsExclude
            autoComplete="email"
            label="Email Address"
            name="email"
            optionalRules={{ pattern: /\S+@\S+\.\S+/ }}
            type="email"
          />
        </Grid>
      )}
      <Grid item xs={12} sm={12}>
        <SelectSavedAddress
          customerAddresses={deduplicatedAddresses}
          defaultAddressId={defaultAddressId}
          loading={loading}
          onSelect={onSavedAddressSelected}
          handleAddNewAddress={handleAddNewAddress}
        />
      </Grid>
      <Grid item xs={6} sm={6}>
        <RequiredTextField
          fsExclude
          name="firstname"
          label="First Name"
          autoComplete="given-name"
          data-cy="address-firstname"
          optionalRules={addressNameRule}
        />
      </Grid>
      <Grid item xs={6} sm={6}>
        <RequiredTextField
          fsExclude
          name="lastname"
          label="Last Name"
          autoComplete="family-name"
          data-cy="address-lastname"
          optionalRules={addressNameRule}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        {autocompleteProps ? (
          <AddressStreetAutocompleteField autocompleteProps={autocompleteProps} />
        ) : (
          <RequiredTextField
            fsExclude
            name="street1"
            label="Address"
            autoComplete="address-line1"
            inputProps={addressMaxLengthProp}
            optionalRules={addressMaxLengthRule}
            data-cy="address1"
          />
        )}
      </Grid>
      <Grid item xs={12} sm={12}>
        <Controller
          render={({ name, onBlur, onChange, ref, value }) => (
            <MuiTextField
              fullWidth
              InputProps={{ disableUnderline: true }}
              inputProps={addressMaxLengthProp}
              label="Apartment, unit, suite, etc. (optional)"
              autoComplete="address-line2"
              inputRef={ref}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
              variant="filled"
            />
          )}
          control={control}
          name="street2"
          // defaultValue=""
          rules={addressMaxLengthRule}
        />
      </Grid>
      <Grid item xs={6} sm={6}>
        <RequiredTextField name="city" label="City" autoComplete="address-level2" data-cy="city" />
      </Grid>
      <Grid item xs={6} sm={6}>
        {regionOptions.length > 0 ? (
          <>
            <SelectField
              autoComplete="address-level1"
              name="region"
              label="State"
              options={regionOptions}
              onChange={onRegionChange}
              rules={{ required: isStateRequired }}
              data-cy="state"
            />
            <input type="hidden" name="region_id" ref={register} />
          </>
        ) : (
          <TextField
            autoComplete="address-level1"
            name="region"
            label="State"
            rules={{ required: isStateRequired }}
          />
        )}
      </Grid>
      <Grid item xs={4} sm={6}>
        <RequiredTextField
          name="postcode"
          label="Zip Code"
          autoComplete="postal-code"
          data-cy="zipcode"
        />
      </Grid>
      <Grid item xs={8} sm={6}>
        <RequiredSelectField
          autoComplete="country"
          disabled={countriesLoading}
          name="country_code"
          label={countriesLoading ? 'Loading countries...' : 'Country'}
          onChange={onCountryChange}
          options={countryOptions}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <RequiredTextField
          fsExclude
          name="telephone"
          label="Phone"
          autoComplete="tel"
          type="tel"
          data-cy="phone"
        />
      </Grid>
    </Grid>
  )
}

export default AddressForm
